import type { JSX } from "react";
import styled from "styled-components";
import t from "../../../shared/translations";
import WithDocs from "../../components/WithDocs";
import { H3, StyledRenderMark } from "../stylings";
import ActionPoints from "./ActionPoints";
import MeanChart from "./MeanChart";
import QuestionChart from "./QuestionChart";
import type { IDoc, ITeamScan } from "./Report";
import SpiderChart from "./SpiderChart";
import Strengths from "./Strengths";
import Weaknesses from "./Weaknesses";

interface IProps {
    teamScan: ITeamScan;
    printing?: boolean;
    docList: IDoc[];
}

const ChartRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    @media (min-width: 600px) {
        grid-template-columns: 4fr 6fr;
    }
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    @media (min-width: 600px) {
        grid-template-columns: 1fr 1fr;
        gap: 2rem;
    }
`;

const PageBreak = styled.div`
    page-break-after: always;
`;

function TPS({
    teamScan: {
        report: { tps },
    },
    printing,
    docList,
}: IProps): JSX.Element {
    const introContent = printing
        ? docList.find((doc) => doc.id === "player.team-scan.report-tps-intro-print").content
        : docList.find((doc) => doc.id === "player.team-scan.report-tps-intro").content;
    return (
        <div>
            <StyledRenderMark content={introContent} />
            {printing && <PageBreak />}
            <H3>{t("shared.team-scan-report.heading-results")}</H3>
            {printing ? (
                <h4>{t("shared.team-scan-report.heading-overview")}</h4>
            ) : (
                <WithDocs
                    component="h4"
                    modal="TeamScan/Info/ReportTPSOverview"
                    docPath="player/team-scan/report-tps-overview"
                >
                    {t("shared.team-scan-report.heading-overview")}
                </WithDocs>
            )}
            <MeanChart
                mean={tps.mean}
                memberMeans={tps.membersMeans}
                labelText={t("shared.team-scan-report.label-psychological-safety")}
            />
            {printing ? (
                <h4>{t("shared.team-scan-report.heading-detailed-view")}</h4>
            ) : (
                <WithDocs
                    component="h4"
                    modal="TeamScan/Info/ReportTPSDetails"
                    docPath="player/team-scan/report-tps-details"
                >
                    {t("shared.team-scan-report.heading-detailed-view")}
                </WithDocs>
            )}
            <h5>{t("shared.team-scan-report.heading-statements")}</h5>
            <ol type="A">
                {tps.questionIds.map((id) => (
                    <li key={id}>{t(`shared.team-scan-report-question.${id}`)}</li>
                ))}
            </ol>
            <ChartRow>
                <div>
                    <SpiderChart values={tps.questionMeans} />
                </div>
                <div>
                    <QuestionChart values={tps.questionMeans} />
                </div>
            </ChartRow>
            {printing && <PageBreak />}
            <h4>{t("shared.team-scan-report.heading-key-takeaways")}</h4>
            <Row>
                <Strengths ids={tps.strengths} allIds={tps.questionIds} />
                <Weaknesses ids={tps.weaknesses} allIds={tps.questionIds} />
            </Row>
            <ActionPoints ids={tps.actionPoints} allIds={tps.questionIds} docList={docList} />
            {printing && <PageBreak />}
        </div>
    );
}

export default TPS;
